import { GlyphMark } from '../entries/GlyphMark.jsx';
import { TokenPill } from '../entries/TokenPill.jsx';
import type { EntryRowProps } from '../../api/adapter';

/** One rapid-log entry, with its tokens flowing inline.
 *
 *  The design system's `EntryRow` stacks the text and the metadata in a
 *  `flexDirection: column`, so tokens always take a second line. Its own frames
 *  do something different: one token sits inline after the text
 *  (`Measure the narrow widths /Product`) and three wrap to the next line only
 *  because they run out of room. That is one wrapping row, not two stacked ones.
 *
 *  So this composes `GlyphMark` and `TokenPill` — the design system's own
 *  primitives, unchanged — into the row its frames actually show. Reported
 *  upstream with the other component notes; when `EntryRow` grows an inline
 *  variant this can go.
 */
/** The state as a word, for the accessible name. `open` is the default and
 *  saying so on every unfinished task would be noise in a screen reader too. */
const STATE_IN_WORDS: Record<string, string | null> = {
  open: null,
  question: null,
  done: 'done',
  migrated: 'migrated',
  cancelled: 'cancelled',
  answered: 'answered',
};

export function EntryLine({
  entry,
  selected,
  queued,
  onPress,
}: {
  entry: EntryRowProps;
  selected?: boolean;
  queued?: boolean;
  onPress?: () => void;
}) {
  const dim = entry.state === 'done' || entry.state === 'migrated';
  const body = (
    <div
      style={{
        display: 'flex',
        gap: 'var(--bujo-space-2)',
        alignItems: 'flex-start',
        minHeight: 'var(--bujo-row-min)',
        padding: '6px 0',
        borderBottom: '1px solid var(--bujo-rule-hairline)',
        background: selected ? 'var(--bujo-select-ground)' : 'transparent',
        boxShadow: selected
          ? 'inset var(--bujo-select-edge-width) 0 0 var(--bujo-select-edge)'
          : 'none',
        paddingLeft: selected ? 'var(--bujo-space-2)' : 0,
        marginLeft: selected ? 'calc(var(--bujo-space-2) * -1)' : 0,
      }}
    >
      <GlyphMark
        state={selected ? 'selected' : entry.state}
        priority={entry.priority && !selected}
        style={{ paddingTop: 2 }}
      />

      {/* One wrapping row: text and tokens share a line until they cannot. */}
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          columnGap: 'var(--bujo-space-2)',
          rowGap: 3,
        }}
      >
        <span
          style={{
            font: 'var(--bujo-fw-regular) var(--bujo-fs-entry)/var(--bujo-lh-row) var(--bujo-font-mono)',
            color:
              entry.state === 'cancelled'
                ? 'var(--bujo-state-cancelled)'
                : dim
                  ? 'var(--bujo-text-dim)'
                  : 'var(--bujo-text-primary)',
            textDecoration: entry.state === 'cancelled' ? 'line-through' : 'none',
            textWrap: 'pretty',
          }}
        >
          {selected && entry.priority ? (
            <span style={{ color: 'var(--bujo-state-priority)', fontWeight: 'var(--bujo-fw-bold)' }}>
              !{' '}
            </span>
          ) : null}
          {entry.text}
        </span>

        {entry.meta ? (
          <span
            style={{
              font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
              color: 'var(--bujo-text-meta)',
            }}
          >
            {entry.meta}
          </span>
        ) : null}

        {entry.tokens.map((token) => (
          <TokenPill key={`${token.kind}:${token.name}`} kind={token.kind} name={token.name} />
        ))}
      </span>

      {/* No `DONE` / `MIGR` / `CANC` badge. The state is already on the row
          three times over — the signifier glyph, the dimming, the strikethrough
          on a cancelled entry, and the `→ date` on a migrated one — so a
          right-aligned abbreviation was noise on every completed line, which is
          most of them by the end of a day.

          The brief's rule is that state is never carried by colour alone, and it
          is not: the glyph is a distinct shape. The *word* it also asks for
          "where space allows" now lives in the accessible name below, so nothing
          is lost to a screen reader.

          `⌁ queued` stays: that is a sync condition rather than a state, it is
          transient, and nothing else on the row says it. `SEL` stays because it
          is the selection affordance the frame draws. */}
      {queued ? (
        <Badge color="var(--bujo-state-queued)">⌁ queued</Badge>
      ) : selected ? (
        <Badge color="var(--bujo-select-edge)">SEL</Badge>
      ) : null}
    </div>
  );

  if (!onPress) return body;
  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={[
        entry.text,
        // Carried here now the badge is gone, so state survives without sight.
        STATE_IN_WORDS[entry.state],
        queued ? 'queued' : null,
        selected ? 'open actions' : null,
      ]
        .filter(Boolean)
        .join(' — ')}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
        color: 'inherit',
        cursor: 'pointer',
      }}
    >
      {body}
    </button>
  );
}

function Badge({ children, color }: { children: unknown; color: string }) {
  return (
    <span
      style={{
        flex: 'none',
        paddingTop: 4,
        font: 'var(--bujo-fw-bold) var(--bujo-fs-badge)/1 var(--bujo-font-mono)',
        letterSpacing: 'var(--bujo-ls-badge)',
        color,
      }}
    >
      {children as never}
    </span>
  );
}
